<template>
  <component
    :is="showLoginText ? 'a' : 'router-link'"
    role="button"
    :disabled="disableCard"
    v-bind="linkTarget"
    :tabindex="disabled ? -1 : 0"
    class="create-campaign-source-item"
    :class="{ 'create-campaign-source-item--login-link': showLoginText }"
    :aria-disabled="disableCard ? 'true' : 'false'"
  >
    <div
      class="create-campaign-source-item__icon"
      :aria-disabled="disabled ? 'true' : 'false'"
      :style="`--icon-background-color: ${iconBackgroundColor}`"
    >
      <slot name="icon" />
    </div>

    <div class="create-campaign-source-item__content">
      <slot />

      <create-campaign-cg-elite-badge v-if="showCgMembersBadge" />
    </div>

    <div v-if="showLoginText" class="create-campaign-source-item__login-text">
      <slot name="login-text" />
    </div>
  </component>
</template>

<script>
import userIsCgElite from '@/mixins/userIsCgElite'
import userIsLoggedIn from '@/mixins/userIsLoggedIn'
import { getLoginPageUrl } from '@/utils/redirectToLoginPage'

import CreateCampaignCgEliteBadge from './CreateCampaignCgEliteBadge.vue'

export default {
  name: 'CreateCampaignSourceItem',
  mixins: [
    userIsCgElite,
    userIsLoggedIn,
  ],
  components: { CreateCampaignCgEliteBadge },
  props: {
    routerName: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    iconBackgroundColor: {
      type: String,
      required: false,
      default: '#AEFAFF',
    },
    isOnlyCgElite: {
      type: Boolean,
      required: false,
      default: false,
    },
    requireLoggedInUser: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    linkTarget () {
      const { routerName, $route, showLoginText } = this

      if (showLoginText) {
        return { href: getLoginPageUrl({ redirect: $route.fullPath }) }
      } else {
        return { to: { name: routerName, params: $route.params } }
      }
    },
    showCgMembersBadge () {
      const { userIsCgElite, isOnlyCgElite, disabled } = this;

      if (userIsCgElite) { return false }
      return isOnlyCgElite && disabled
    },
    showLoginText () {
      const { requireLoggedInUser, userIsLoggedIn } = this
      return requireLoggedInUser && !userIsLoggedIn
    },
    disableCard () {
      const { showLoginText, disabled } = this

      if (showLoginText) { return false }
      return disabled
    },
  },
}
</script>

<style lang="scss" scoped>
.create-campaign-source-item {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  padding: 30px 20px;
  width: 242px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 16px;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.08);
  color: #222325;
  text-align: center;
  font-family: 'Lato-Italic', sans-serif;
  font-size: 15px;
  font-style: italic;
  font-weight: 400;
  line-height: normal;
  background-color: #fff;
  border: 4px solid transparent;
  transition: background-color ease-in-out 0.36s;

  &:hover,
  &:focus {
    background-color: #F6F5FF;
    outline: none;
  }

  &:active {
    border: 4px solid #000;
  }

  &[aria-disabled="true"] {
    cursor: default;
    pointer-events: none;
    background-color: #F4F4F4;
  }

  &--login-link {
    cursor: default;
    background-color: #F4F4F4 !important;
    outline: none;
  }

  &__icon {
    width: 40px;
    height: 40px;
    display: flex;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    color: #222325;
    align-items: center;
    justify-content: center;
    background-color: var(--icon-background-color, #AEFAFF);

    &[aria-disabled="true"] {
      background-color: #D3D2D2;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  &__login-text::v-deep > span {
    color: #6E50FF;
    cursor: pointer;
  }

  @media screen and (max-width: 430px) {
    width: 100%;
    padding: 24px;
  }
}
</style>
